import { useMutation, useQuery } from '@apollo/client';
import { fetchLatLongFromCityPostalCode } from '@components/InputCity/hereApi';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useAuth } from '@contexts/AuthContext';
import { ADD_JOB_OFFER_PRO, ADD_JOB_OFFER_UPERCUT, ADD_JOB_OFFER_DRAFT_PRO, ADD_JOB_OFFER_DRAFT_UPERCUT, UPDATE_JOB_OFFER_PRO, UPDATE_JOB_OFFER_UPERCUT, GET_JOB_OFFER, GET_JOB_OFFER_PRO, GET_JOB_OFFERS, GET_JOB_OFFERS_PRO } from '@gql/jobs';
import { GET_PRO } from '@gql/pros';
import { isNull, isUndefined } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

interface UseSaveJobOfferOptions {
    proId: string;
    jobOffer?: any;
    onSuccess?: (values: any) => void;
    onError?: (error: any) => void;
}

export const buildPayload = (data: any, defaultValues: any) => ({
    _id: defaultValues?._id ?? undefined,
    title: data?.title,
    loc: undefined as any,
    offre_cachee: data?.offre_cachee,
    brandId: data?.brandId,
    showPhotos: data?.showPhotos,
    showVideo: data?.showVideo,
    publishDate: data?.publishDate,
    city: data?.city,
    featureId: data?.featureId,
    postalCode: data?.postalCode,
    address: data?.address,
    enablePublishDate: data?.enablePublishDate,
    assigneeIds: data?.assignees?.map(item => item._id),
    position: {
        companyName: data?.position?.companyName,
        professions: data?.position?.professions,
        numberOfPositions: data?.position?.numberOfPositions
            ? parseInt(data?.position?.numberOfPositions)
            : null,
        place: data?.position?.place,
        allowFreshGrad: data?.position?.allowFreshGrad,
        experience: data?.position?.experience,
        hasTeam: data?.position?.hasTeam ?? false,
        teamNumber: parseInt(data?.position?.teamNumber),
    },
    contract: {
        status: data?.contract?.status,
        contractType: data?.contract?.contractType,
        startDate: data?.contract?.startDate,
        endDate: data?.contract?.endDate,
        rateType: data?.contract?.rateType,
        isAnnualSalary: data?.contract?.isAnnualSalary,
        hourPerWeek: data?.contract?.rateType === 'hourly' ? parseInt(data?.contract?.hourPerWeek) : null,
        dayPerWeek: data?.contract?.dayPerWeek
            ? data?.contract?.dayPerWeek?.map(item => parseFloat(item))?.sort()
            : [5],
        monthPerYear: data?.contract?.monthPerYear ? parseInt(data?.contract?.monthPerYear) : null,
        benefits: data?.contract?.benefits,
        dayOff: {
            number: data?.contract?.dayOff?.number ? parseInt(data?.contract?.dayOff?.number) : null,
            unit: data?.contract?.dayOff?.unit,
        },
        rtt: {
            isActive: data?.contract?.rtt?.isActive ?? false,
            days: parseInt(data?.contract?.rtt?.days),
        },
        guaranteedSalary: {
            min: data?.contract?.guaranteedSalary?.min
                ? parseInt(data?.contract?.guaranteedSalary?.min)
                : null,
            max: data?.contract?.guaranteedSalary?.max
                ? parseInt(data?.contract?.guaranteedSalary?.max)
                : null,
            type: data?.contract?.guaranteedSalary?.type ?? 'Brut',
        },
        commission: data?.contract?.commission?.map(item => ({
            amount: item?.amount ? parseInt(item?.amount) : null,
            origin: item?.origin,
        })),
        bonus: [
            {
                type: Array.isArray(data?.contract?.bonus)
                    ? data?.contract?.bonus?.[0]?.type ?? null
                    : data?.contract?.bonus ?? null,
                max: Number(data?.contract?.commissions) ?? null,
            },
        ],
        showSalary: data?.contract?.showSalary,
        remuneration: {
            min: data?.contract?.remuneration?.min ? parseInt(data?.contract?.remuneration?.min) : null,
            max: data?.contract?.remuneration?.max ? parseInt(data?.contract?.remuneration?.max) : null,
        },
        showBonus: data?.contract?.showBonus,
    },
    candidate: {
        softSkills: data?.candidate?.softSkills,
        softSkillIds: data?.candidate?.softSkillIds,
        specialties: data?.candidate?.specialties,
        degree: data?.candidate?.degree ?? null,
        languages:
            data?.candidate?.languages
                ?.filter(item => !!item.languageId && !!item.levelId)
                .map(item => ({
                    languageId: item.languageId,
                    levelId: item.levelId,
                    mandatory: item.mandatory,
                })) ?? [],
    },
    description: {
        missions: data?.description?.missions,
        advantages: data?.description?.advantages,
        listAdvantages: data?.description?.listAdvantages ?? [],
    },
    recruitment: {
        place: data?.recruitment?.place ?? false,
        quickResponse: data?.recruitment?.quickResponse ?? false,
        mode: data?.recruitment?.mode ?? false,
        attempt: data?.recruitment?.attempt ?? false,
        method: data?.recruitment?.method ?? 'direct_email',
        premiumFeatures: data?.recruitment?.premiumFeatures ?? [],
        steps: data?.recruitment?.steps ?? [],
        interviewDate: data?.recruitment?.interviewDate?.map(item => ({
            date: item?.date,
            time: {
                start: item?.time?.start,
                end: item?.time?.end,
            },
        })),
        duration: data?.recruitment?.duration,
    },
    assignees: data?.assignees?.map(item => ({
        _id: item?._id,
        assigneeType: item?.assigneeType,
        role: item?.role,
    })),
});

export const useSaveJobOffer = ({ proId, jobOffer, onSuccess, onError }: UseSaveJobOfferOptions = {
    proId: null
}) => {
    const { isUpercut } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [data, setData] = useState<any>({});

    const isUpdate = !!jobOffer?._id;
    const isUpercutFlow = isUpercut;

    const { data: proData } = useQuery(GET_PRO, {
        variables: {
            proId: proId,
        },
        skip: !proId,
    });

    const pro = proData?.getPro;

    const premiumFeatures = useMemo(() => {
        const features = jobOffer?.recruitment?.premiumFeatures ?? [];
        if (pro?.config.recruitmentMethod === 'premium') {
            if (pro?.config?.dismissalGuarantee && !features.includes('dismissalGuarantee')) {
                features.push('dismissalGuarantee');
            }
            if (pro?.config?.resignationGuarantee && !features.includes('resignationGuarantee')) {
                features.push('resignationGuarantee');
            }
            if (!pro?.config?.dismissalGuarantee && !pro?.config?.resignationGuarantee) {
                features.push('noGuarantee');
            }
        }
        return features;
    }, [pro?.config, jobOffer?.recruitment?.premiumFeatures]);

    const defaultValues = useMemo(() => {
        const data = {
            _id: jobOffer?._id ?? undefined,
            assignees: jobOffer?.assignees ?? [],
            title: jobOffer?.title,
            offre_cachee: jobOffer?.offre_cachee ?? false,
            brandId: jobOffer?.brandId,
            showPhotos: jobOffer?.showPhotos,
            showVideo: jobOffer?.showVideo,
            publishDate: jobOffer?.publishDate,
            city: jobOffer?.city,
            featureId: jobOffer?.featureId,
            address: jobOffer?.address,
            postalCode: jobOffer?.postalCode,
            enablePublishDate: jobOffer?.enablePublishDate,
            position: {
                companyName: jobOffer?.position?.companyName,
                professions: jobOffer?.position?.professions,
                numberOfPositions: jobOffer?.position?.numberOfPositions ?? 1,
                place: jobOffer?.position?.place,
                allowFreshGrad:
                    isNull(jobOffer?.position?.allowFreshGrad) ||
                        isUndefined(jobOffer?.position?.allowFreshGrad)
                        ? true
                        : jobOffer?.position?.allowFreshGrad,
                experience: jobOffer?.position?.experience,
                hasTeam: jobOffer?.position?.hasTeam ?? false,
                teamNumber: jobOffer?.position?.teamNumber
                    ? parseInt(jobOffer?.position?.teamNumber)
                    : null,
            },
            contract: {
                status: jobOffer?.contract?.status,
                contractType: jobOffer?.contract?.contractType,
                startDate: jobOffer?.contract?.startDate,
                endDate: jobOffer?.contract?.endDate,
                rateType: jobOffer?.contract?.rateType ?? 'hourly',
                isAnnualSalary: jobOffer?.contract?.isAnnualSalary ?? false,
                hourPerWeek: jobOffer?.contract?.hourPerWeek
                    ? parseInt(jobOffer?.contract?.hourPerWeek)
                    : 35,
                dayPerWeek: jobOffer?.contract?.dayPerWeek
                    ? jobOffer?.contract?.dayPerWeek?.map(item => parseFloat(item))
                    : [5],
                monthPerYear: jobOffer?.contract?.monthPerYear
                    ? parseInt(jobOffer?.contract?.monthPerYear)
                    : 12,
                benefits: jobOffer?.contract?.benefits,
                dayOff: {
                    number: jobOffer?.contract?.dayOff?.number
                        ? parseInt(jobOffer?.contract?.dayOff?.number)
                        : 25,
                    unit: jobOffer?.contract?.dayOff?.unit ?? 'Jours',
                },
                rtt: {
                    isActive: jobOffer?.contract?.rtt?.isActive ?? false,
                    days: jobOffer?.contract?.rtt?.days ? parseInt(jobOffer?.contract?.rtt?.days) : null,
                },
                guaranteedSalary: {
                    min: jobOffer?.contract?.guaranteedSalary?.min
                        ? parseInt(jobOffer?.contract?.guaranteedSalary?.min)
                        : null,
                    max: jobOffer?.contract?.guaranteedSalary?.max
                        ? parseInt(jobOffer?.contract?.guaranteedSalary?.max)
                        : null,
                    type: jobOffer?.contract?.guaranteedSalary?.type ?? 'Brut',
                },
                commission: jobOffer?.contract?.commission?.map(item => ({
                    amount: item?.amount ? parseInt(item?.amount) : null,
                    origin: item?.origin,
                })),
                bonus: jobOffer?.contract?.bonus?.map(item => item?.type ?? null)?.[0] ?? undefined,
                showSalary:
                    isUndefined(jobOffer?.contract?.showSalary) || isNull(jobOffer?.contract?.showSalary)
                        ? true
                        : jobOffer?.contract?.showSalary,
                remuneration: {
                    min: jobOffer?.contract?.remuneration?.min
                        ? parseInt(jobOffer?.contract?.remuneration?.min)
                        : null,
                    max: jobOffer?.contract?.remuneration?.max
                        ? parseInt(jobOffer?.contract?.remuneration?.max)
                        : null,
                },
                showBonus: jobOffer?.contract?.showBonus,
            },
            candidate: {
                softSkills: jobOffer?.candidate?.softSkills,
                softSkillIds: jobOffer?.candidate?.softSkillIds,
                specialties: jobOffer?.candidate?.specialties,
                degree: jobOffer?.candidate?.degree ?? null,
                languages: jobOffer?.candidate?.languages,
            },
            description: {
                missions: jobOffer?.description?.missions,
                advantages: jobOffer?.description?.advantages,
                listAdvantages: jobOffer?.description?.listAdvantages ?? [],
            },
            recruitment: {
                place: jobOffer?.recruitment?.place ?? false,
                quickResponse: jobOffer?.recruitment?.quickResponse ?? false,
                mode: jobOffer?.recruitment?.mode ?? false,
                attempt: jobOffer?.recruitment?.attempt ?? false,
                method: jobOffer?.recruitment?.method ?? 'direct_email',
                premiumFeatures: premiumFeatures,
                steps: jobOffer?.recruitment?.steps ?? [],
                interviewDate: jobOffer?.recruitment?.interviewDate?.map(item => {
                    return {
                        date: item?.date,
                        time: {
                            start: item?.time?.start,
                            end: item?.time?.end,
                        },
                    };
                }),
                duration: jobOffer?.recruitment?.duration,
            },
        };
        return data;
    }, [jobOffer, premiumFeatures]);


    const methods = useForm({
        mode: 'all',
        defaultValues,
    });

    const [saveDraftMutation] = useMutation(isUpercutFlow ? ADD_JOB_OFFER_DRAFT_UPERCUT : ADD_JOB_OFFER_DRAFT_PRO, {
        onCompleted: result => {
            const savedJobOffer = isUpercutFlow ? result?.addJobOfferDraftUpercut : result?.addJobOfferDraftPro;
            enqueueSnackbar('Brouillon enregistré avec succès', { variant: 'success' });
            setData(savedJobOffer);
            setSuccess(true);
            setLoading(false);
            methods.reset(methods.getValues());
            onSuccess?.(savedJobOffer);
        },
        onError: error => {
            enqueueSnackbar(error.message, { variant: 'error' });
            setSuccess(false);
            setLoading(false);
            onError?.(error);
        },
    });

    const [save] = useMutation(isUpercutFlow ? ADD_JOB_OFFER_UPERCUT : ADD_JOB_OFFER_PRO, {
        refetchQueries: [
            isUpercutFlow ? GET_JOB_OFFERS : GET_JOB_OFFERS_PRO,
        ],
        onCompleted: result => {
            const savedJobOffer = isUpercutFlow ? result?.addJobOfferUpercut : result?.addJobOfferPro;
            enqueueSnackbar('Offre ajoutée avec succès', { variant: 'success' });
            setData(savedJobOffer);
            setSuccess(true);
            setLoading(false);
            methods.reset(methods.getValues());
            onSuccess?.(savedJobOffer);
        },
        onError: error => {
            enqueueSnackbar(error.message, { variant: 'error' });
            setSuccess(false);
            setLoading(false);
            onError?.(error);
        },
    });

    const [update] = useMutation(isUpercutFlow ? UPDATE_JOB_OFFER_UPERCUT : UPDATE_JOB_OFFER_PRO, {
        refetchQueries: [
            isUpercutFlow ? GET_JOB_OFFER : GET_JOB_OFFER_PRO,
            isUpercutFlow ? GET_JOB_OFFERS : GET_JOB_OFFERS_PRO,
        ],
        onCompleted: result => {
            const updatedJobOffer = isUpercutFlow ? result?.updateJobOfferUpercut : result?.updateJobOfferPro;
            enqueueSnackbar('Offre modifiée avec succès', { variant: 'success' });
            setData(updatedJobOffer);
            setSuccess(true);
            setLoading(false);
            methods.reset(methods.getValues());
            onSuccess?.(updatedJobOffer);
        },
        onError: error => {
            enqueueSnackbar(error.message, { variant: 'error' });
            setSuccess(false);
            setLoading(false);
            onError?.(error);
        },
    });

    const submit = useCallback(
        async (formData: any) => {
            setLoading(true);
            const mutate = isUpdate ? update : save;

            const payload = buildPayload(formData, defaultValues);

            const cityChanged = formData?.city !== defaultValues?.city;
            const postalChanged = formData?.postalCode !== defaultValues?.postalCode;

            if (cityChanged || postalChanged) {
                const result = await fetchLatLongFromCityPostalCode(formData?.city, formData?.postalCode);
                payload.loc = {
                    type: 'Point',
                    coordinates: [result?.items?.[0]?.position?.lng, result?.items?.[0]?.position?.lat],
                };
            } else {
                payload.loc = jobOffer?.loc;
            }

            await mutate({
                variables: { payload },
            });
        },
        [isUpdate, update, save, defaultValues, jobOffer],
    );

    const saveDraft = useCallback(
        async (formData: any) => {
            setLoading(true);
            const payload = buildPayload(formData, defaultValues);

            const cityChanged = formData?.city !== defaultValues?.city;
            const postalChanged = formData?.postalCode !== defaultValues?.postalCode;

            if (cityChanged || postalChanged) {
                const result = await fetchLatLongFromCityPostalCode(formData?.city, formData?.postalCode);
                payload.loc = {
                    type: 'Point',
                    coordinates: [result?.items?.[0]?.position?.lng, result?.items?.[0]?.position?.lat],
                };
            } else {
                payload.loc = jobOffer?.loc;
            }

            await saveDraftMutation({
                variables: { payload },
            });
        },
        [saveDraftMutation, defaultValues, jobOffer],
    );

    return {
        submit,
        saveDraft,
        loading,
        success,
        setSuccess,
        data,
        isUpdate,
        methods,
    };
};
